import React, { useContext, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Avatar,
  Paper,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const REQUIRED_FIELDS = ['familyId', 'familyMemberId'];

const findMissingFields = (person) => {
  if (!person) return ['(no person)'];
  const missing = [];
  REQUIRED_FIELDS.forEach((key) => {
    const val = person[key];
    if (val === undefined || val === null || String(val).trim() === '') {
      missing.push(key);
    }
  });
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  if (!name) missing.push('name');
  const upnSuffix = String(person.familyMemberId || '')
    .split('-')
    .pop();
  const upn = parseInt(upnSuffix, 10);
  if (!Number.isFinite(upn) || upn <= 0) missing.push('upn');
  return missing;
};

export default function MergePersonSelectModal({
  open,
  personA,
  personB,
  onSelect,
  onClose
}) {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext) || {};

  const hasValidatedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasValidatedRef.current = false;
      return;
    }
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;

    const missingA = findMissingFields(personA);
    const missingB = findMissingFields(personB);

    if (missingA.length === 0 && missingB.length === 0) return;

    const nameA =
      `${personA?.firstName || ''} ${personA?.lastName || ''}`.trim() ||
      personA?.familyMemberId ||
      'Person A';
    const nameB =
      `${personB?.firstName || ''} ${personB?.lastName || ''}`.trim() ||
      personB?.familyMemberId ||
      'Person B';

    const parts = [];
    if (missingA.length > 0) parts.push(`${nameA}: ${missingA.join(', ')}`);
    if (missingB.length > 0) parts.push(`${nameB}: ${missingB.join(', ')}`);

    setSnack?.({
      status: true,
      msg: t('Cannot start merge — missing required details ({{parts}}).', {
        parts: parts.join(' · ')
      }),
      severity: 'error'
    });
    onClose?.();
  }, [open, personA, personB, setSnack, onClose]);

  const renderCard = (person, otherPerson) => (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        borderRadius: 3,
        p: 3,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Avatar
        sx={{
          width: 48,
          height: 48,
          mb: 1.5,
          bgcolor: 'primary.main'
        }}
      >
        <PersonIcon />
      </Avatar>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 700
        }}
      >
        {person.firstName} {person.lastName}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          mb: 2,
          width: '100%'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary'
          }}
        >
          <strong>{t('ID:')}</strong> {person.familyMemberId}
        </Typography>
        {person.upn && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            <strong>{t('UPN:')}</strong> {person.upn}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary'
          }}
        >
          <strong>{t('Family:')}</strong> {person.familyId}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        color="success"
        size="small"
        startIcon={<CheckCircleIcon />}
        sx={{
          mt: 'auto',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3
        }}
        onClick={() => onSelect({ retained: person, deleted: otherPerson })}
      >
        {t('Keep this person')}
      </Button>
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 3, overflow: 'hidden' }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'block',
          textAlign: 'center',
          py: 2,
          fontWeight: 700,
          fontSize: '1.25rem'
        }}
      >
        {t('Select Person to Keep')}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            mt: 3,
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { fontSize: '0.82rem' }
          }}
        >
          {t(
            'Pedigree relationships for the deleted person will not be automatically transferred. You will need to review and correct the pedigree after merging.'
          )}
        </Alert>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'stretch'
          }}
        >
          {renderCard(personA, personB)}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.5
            }}
          >
            <Tooltip title={t('Choose a side')}>
              <SwapHorizIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
            </Tooltip>
          </Box>
          {renderCard(personB, personA)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
